"use client";

import { useState, useEffect, useRef } from 'react';
import classes from './Glossary.module.scss';
import SearchIcon from '@/assets/icons/searchIcon';
import { glossaryTerms } from '@/data/glossaryTerms';

const Glossary = ({term} : {term?: string}) => {
  const [searchQuery, setSearchQuery] = useState<string>(''); 
  const [filteredTerms, setFilteredTerms] = useState(glossaryTerms);
  const [selectedLetter, setSelectedLetter] = useState<string>('All');
  const [selectedTerm, setSelectedTerm] = useState<any>(null);

  const popupRef = useRef<HTMLDivElement>(null);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    if (query) {
      setFilteredTerms(
        glossaryTerms.filter((term) =>
          term.term.toLowerCase().includes(query.toLowerCase())
        )
      );
    } else {
      setFilteredTerms(glossaryTerms);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setFilteredTerms(glossaryTerms);
  };

//   const handleLetterSelect = (letter: string) => {
//     setSelectedLetter(letter);
//     if (letter === 'All') {
//       setFilteredTerms(glossaryTerms);
//     } else {
//       setFilteredTerms(
//         glossaryTerms.filter((term) => term.term[0].toUpperCase() === letter)
//       );
//     }
//   };

  const handleLetterSelect = (letter: string) => {
    setSelectedLetter(letter);
    if (letter === 'All') {
      setFilteredTerms(glossaryTerms);
    } else if (letter === '#') {
      setFilteredTerms(
        glossaryTerms.filter((term) => /^[0-9]/.test(term.term)) // Check if term starts with a number
      );
    } else {
      setFilteredTerms(
        glossaryTerms.filter((term) => term.term[0].toUpperCase() === letter)
      );
    }
  };

  const handleTermClick = (term: { term: string, definition: string }) => {
    setSelectedTerm(term);
    const formattedTerm = term.term.toLowerCase().replace(/\s+/g, '-');
    window.history.pushState(null, '', `/glossary/${formattedTerm}`);
  };

  useEffect(() => {    
    const selectedTerm = glossaryTerms.find(({term: glossaryTerm}) => { 
      return glossaryTerm.toLowerCase().replace(/\s+/g, '-') == term
    });

    if(selectedTerm) {
      setSelectedTerm(selectedTerm);
    }
  }, [term]);


  const handleClosePopup = () => {
    setSelectedTerm(null);
    window.history.pushState(null, '', '/glossary');
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
      handleClosePopup();
    }
  };

  useEffect(() => {
    if (!searchQuery) {
      handleLetterSelect(selectedLetter);
    }
  }, [searchQuery, selectedLetter]);  

  return (
    <div className={classes["glossaryContainer"]}>
      <h1 className={classes["title"]}>Glossary</h1>
      <p className={classes["description"]}>
        Explore our extensive glossary of Sustainability terms. Enhance your understanding of industry terminology<br /> and stay informed on essential concepts in the field.
      </p>

      <div className={classes["alphabetNav"]}>
        <span
          onClick={() => handleLetterSelect('All')}
          className={`${classes["alphabetOption"]} ${selectedLetter === 'All' ? classes["selected"] : ''}`}
        >
          All
        </span>
        <span className={classes["seperator-2"]}></span>
        {['#','A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'].map((letter) => (
          <span
            key={letter}
            onClick={() => handleLetterSelect(letter)}
            className={`${classes["alphabetOption"]} ${selectedLetter === letter ? classes["selected"] : ''}`}
          >
            {letter}
          </span>
        ))}
      </div>

      <div className={classes["searchWrapper"]}>
        <div className={classes["searchInput"]}>
          <span className={classes["searchIcon"]}>
            <SearchIcon/>
          </span>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            className={classes["searchField"]}
          />
          <span className={classes["clearIcon"]} onClick={handleClearSearch}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path 
                d="M18 6L6 18M6 6L18 18" 
                stroke="var(--text-theme)" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
              />
            </svg>
          </span>
        </div>
      </div>

      <div className={classes["seperator"]}></div>

      <div className={classes["glossaryList"]}>
        <div className={classes["termCardsRow"]}>
        {filteredTerms.map((term, index) => (
            <div 
                key={index} 
                className={classes["termCard"]} 
                onClick={() => term.definition && handleTermClick(term)}
            >
              <h3 className={classes["term"]}>{term.term}</h3>
              <p className={classes["definition"]}>{term.definition}</p>
            </div>
          ))}
        </div>
      </div>

      {selectedTerm && (
        <div className={classes["popupBackdrop"]} onClick={handleBackdropClick}>
          <div className={classes["popupContent"]} ref={popupRef}>
            <button className={classes["closeButton"]} onClick={handleClosePopup}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path 
                    d="M18 6L6 18M6 6L18 18" 
                    stroke="var(--text-theme)" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                />
                </svg>
            </button>
            <h3 className={classes["term"]}>{selectedTerm.term}</h3>
            <p className={classes["definition"]}>{selectedTerm.definition}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Glossary;